import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { API_URL } from '../../utils/apiConfig';

export default function AdminVerify() {
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const verifyToken = useCallback(async () => {
    if (!token) {
      setStatus('error');
      setError('No token provided');
      return;
    }

    let responseData;
    let responseOk;

    try {
      const response = await fetch(`${API_URL}/api/admin/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      responseOk = response.ok;
      responseData = await response.json();
    } catch (networkError) {
      setStatus('error');
      setError('Network error: Unable to connect to the server');
      return;
    }

    if (!responseOk) {
      setStatus('error');
      setError(responseData?.detail || 'Verification failed');
      return;
    }

    // Store the admin token
    localStorage.setItem('pecash_token', responseData.token);
    localStorage.setItem('pecash_admin', 'true');
    
    // Redirect to admin dashboard
    setStatus('success');
    navigate(responseData.redirect || '/admin/dashboard');
  }, [token, navigate]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        {status === 'verifying' && (
          <div className="bg-slate-800 border border-slate-700 rounded-3xl p-8">
            <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Verifying Admin Access</h2>
            <p className="text-slate-400">Please wait while we authenticate your session...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-slate-800 border border-red-800 rounded-3xl p-8">
            <div className="w-16 h-16 rounded-full bg-red-900/50 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Verification Failed</h2>
            <p className="text-red-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/admin')}
              className="px-6 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700"
            >
              Back to Admin Login
            </button>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-slate-800 border border-slate-700 rounded-3xl p-8">
            <div className="w-16 h-16 rounded-full bg-green-900/50 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✓</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Verified</h2>
            <p className="text-slate-400">Redirecting to dashboard...</p>
          </div>
        )}
      </div>
    </div>
  );
}
